import * as Cookies from 'js-cookie';
import {
  CookieServiceOptions,
  createEnsightenConsentManager,
} from '@volkswagen-onehub/audi-cookie-service';
import { AudiColaServiceOptions } from '@volkswagen-onehub/audi-cola-service';
import { AudiConfiguratorLinkServiceOptions } from '@volkswagen-onehub/audi-configurator-link-service';
import { AudiMarketContextServiceOptions } from '@volkswagen-onehub/audi-market-context-service';
import { CurrentCarlineServiceConfig } from '@volkswagen-onehub/audi-current-carline-service';
import { DisclaimerManagerOptions } from '@volkswagen-onehub/disclaimer-manager';
import { EnvConfigServiceOptions } from '@volkswagen-onehub/audi-env-config-service/dist/EnvConfigService';
import { GfaServiceConfigProviderV1 } from '@volkswagen-onehub/gfa-service-config-provider';
import { I18NServiceOptions } from '@volkswagen-onehub/audi-i18n-service';
import { IAuthServiceOptions } from '@volkswagen-onehub/audi-auth-service';
import { LocaleServiceConfig } from '@volkswagen-onehub/locale-service';
import { NavigationServiceOptions } from '@volkswagen-onehub/navigation-service';
import { RenderMode } from '@volkswagen-onehub/audi-render-mode-service';
import { TrackingServiceOptions } from '@volkswagen-onehub/audi-tracking-service';
import { VueFormatterServiceOptions } from '@volkswagen-onehub/audi-vue-formatter-service';
import { createFallbackConsentManager } from './fallback-consent-manager';
import { createLogger } from '../logger';
import { getSysEnv } from './sysenv';
import { removePrivateData } from './tracking-url-sanitizer';

const defaultLogoutRedirectUrl = (): string => location.href;

const metaValues = {};

const getApiGatewayUrl = (dom: Element): string =>
  getMetaContent(dom, 'wag-baseurl');

const getAuthProxyUrls = (
  dom: Element,
): {
  dev: string;
  live: string;
  pre: string;
  tui: string;
} => {
  let liveProxyUrl: string, preLiveProxyUrl: string;
  switch (getLocaleOptions(dom).countryCode) {
    case 'DE':
      liveProxyUrl = 'https://www.audi.de/userinfo/';
      preLiveProxyUrl = 'https://pre-www.audi.de/userinfo2/';
      break;
    case 'US':
      liveProxyUrl = 'https://www.audiusa.com/userinfo/';
      preLiveProxyUrl = 'https://qa.audiusa.com/userinfo/';
      break;
    case 'CA':
      liveProxyUrl = 'https://www.audi.ca/userinfo/';
      preLiveProxyUrl = 'https://pre-www.audi.ca/userinfo1/';
      break;
    default:
      liveProxyUrl = 'https://userinfo.my.audi.com/';
      preLiveProxyUrl = 'https://qa-userinfo.my.audi.com/';
  }
  return {
    dev: 'https://1grbw0.apps.emea.vwapps.io/',
    live: liveProxyUrl,
    pre: preLiveProxyUrl,
    tui: 'https://qa2-userinfo.my.audi.com/',
  };
};

const getMetaContent = (dom: Element, metaName: string): string => {
  if (!metaValues[metaName] || dom != metaValues[metaName].dom) {
    const metatag = dom.querySelector(`meta[name=${metaName}]`);
    metaValues[metaName] = {
      dom,
      value: metatag?.getAttribute('content') || '',
    };
  }
  return metaValues[metaName].value;
};

const getAuthOptions = (dom: Element): IAuthServiceOptions => {
  const sysEnv = getSysEnv(getApiGatewayUrl(dom));
  const proxyUrls = getAuthProxyUrls(dom);
  const authProxyUrl = proxyUrls[sysEnv]; // token-proxy, deployed to serenity from pi-oneaudi branch

  return {
    authProxyRedirectUrl: location.href,
    authProxyUrl,
    defaultLogoutRedirectUrl,
    fagScopeMappings: new Map<string, string[]>([['myaudi', []]]),
  };
};

const getDpuApiGatewayUrl = (dom: Element): string => {
  const apiGateWayUrl = getApiGatewayUrl(dom);
  const localeServiceDefinition = getLocaleOptions(dom);
  const language = localeServiceDefinition.language;
  const country = localeServiceDefinition.countryCode.toLowerCase();

  return `${apiGateWayUrl}/dpu/v1/${country}/${language}`;
};

const getMarketContextApiGatewayUrl = (dom: Element): string => {
  const apiGateWayUrl = getApiGatewayUrl(dom);
  const localeServiceDefinition = getLocaleOptions(dom);
  const language = localeServiceDefinition.language;
  const country = localeServiceDefinition.countryCode.toLowerCase();

  return `${apiGateWayUrl}/market-context/v1/${country}/${language}`;
};

const getPreviewToken = (dom: Element): string => {
  let jwtToken = '';

  const isPreview = Cookies.get('nemo_preview');

  if (isPreview === 'true') {
    const locale = getLocaleOptions(dom);
    const jwtCookieName = `jwt_${locale.countryCode}_${locale.language}`;
    jwtToken = Cookies.get(jwtCookieName);
  }

  return jwtToken;
};

const getColaOptions = (dom: Element): AudiColaServiceOptions => {
  const dpuApiGatewayUrl = getDpuApiGatewayUrl(dom);
  const colaOptions = {
    colaApiKey: 'R4HnYDix9V4etVLxVw9ZqaQKnoh8Bwjp2fqgOz3i', // temorarily hard-code as hotfix // getMetaContent(dom, 'cola-api-key'),
    colaDataVersionsUrl: `${dpuApiGatewayUrl}/versions`,
    colaUrl: getMetaContent(dom, 'cola-api-url'),
  };

  const jwtToken = getPreviewToken(dom);
  if (jwtToken) {
    colaOptions['preview'] = {
      jwtToken,
    };
  }
  return colaOptions;
};

const getConfiguratorLinkOptions = (
  dom: Element,
): AudiConfiguratorLinkServiceOptions => {
  const apiGateWayUrl = getApiGatewayUrl(dom);
  const localeServiceDefinition = getLocaleOptions(dom);
  const language = localeServiceDefinition.language;
  const country = localeServiceDefinition.countryCode.toLowerCase();

  return {
    apiGateWayUrl: `${apiGateWayUrl}/carlinelinks/v1/${country}/${language}`,
  };
};

const getCurrentCarlineOptions = (
  dom: Element,
): CurrentCarlineServiceConfig => {
  return {
    carline: getMetaContent(dom, 'carline'),
    carlineGroup: getMetaContent(dom, 'carlinegroup'),
    prstring: getMetaContent(dom, 'prstring'),
  };
};

const getEnvConfigOptions = (dom: Element): EnvConfigServiceOptions => {
  const sysEnv = getSysEnv(getApiGatewayUrl(dom));
  const envConfigBaseUrl = 'https://assets.audi.com/audi-env-config/0';

  // country specific cases
  const localeServiceDefinition = getLocaleOptions(dom);

  let templateUrl = `${envConfigBaseUrl}/config/${sysEnv}/{key}.json`;
  // us / AoA?
  if (localeServiceDefinition.countryCode.toLowerCase() === 'us') {
    templateUrl = `${envConfigBaseUrl}/config/aoa/${sysEnv}/{key}.json`;
  }

  return {
    templateUrl,
  };
};

const getGfaServiceConfig = (): GfaServiceConfigProviderV1 => {
  const logger = createLogger();

  if (typeof window.SETUPS === 'undefined') {
    logger.info(
      '`window.SETUPS` is not defined. Some services may not retrieve data.',
    );
    return { gfa: {} };
  }

  const gfaServiceConfig = window.SETUPS.get('gfaServiceConfig');

  if (typeof gfaServiceConfig !== 'string' || gfaServiceConfig === '') {
    return { gfa: {} };
  }
  try {
    return JSON.parse(gfaServiceConfig);
  } catch (err) {
    logger.error(err);
    return { gfa: {} };
  }
};

const getDisclaimerManagerOptions = (): DisclaimerManagerOptions => {
  return {
    defaultLegalEntity: 'audi',
    legalEntityLabels: { audi: 'Audi' },
  };
};

const getI18nOptions = (dom: Element): I18NServiceOptions => {
  return {
    i18nEndpointUrl: getMetaContent(dom, 'i18n-endpoint'),
  };
};

const getLocaleOptions = (dom: Element): LocaleServiceConfig => {
  return {
    countryCode: getMetaContent(dom, 'locale-market') || 'DE',
    currency: getMetaContent(dom, 'locale-currency') || 'EUR',
    language: getMetaContent(dom, 'locale-language') || 'de',
  };
};

const getMarketContextServiceOptions = (
  dom: Element,
): AudiMarketContextServiceOptions => {
  return {
    v2: {
      apiGatewayUrl: getMarketContextApiGatewayUrl(dom),
    },
  };
};

const getNavigationOptions: () => NavigationServiceOptions = () => {
  const logger = createLogger();

  if (typeof window.SETUPS === 'undefined') {
    logger.info(
      '`window.SETUPS` is not defined. Some services may not retrieve data.',
    );
    return {};
  }

  const navigationServiceOptions = window.SETUPS.get(
    'navigationServiceOptions',
  );

  if (
    typeof navigationServiceOptions !== 'string' ||
    navigationServiceOptions === ''
  ) {
    return {};
  }
  try {
    return JSON.parse(navigationServiceOptions);
  } catch (err) {
    logger.error(err);
    return {};
  }
};

const getRenderModeOptions = (): RenderMode => {
  const renderMode = document.body.className.includes('cq-wcm-edit')
    ? RenderMode.EDIT
    : RenderMode.DEFAULT;
  return renderMode;
};

const getTrackingOptions = (): TrackingServiceOptions => {
  const logger = createLogger();

  return {
    datalayer: window.digitalData,
    sanitizeUrl: (url: string): string => removePrivateData(url, logger),
  };
};

const getVueFormatterOptions = (dom: Element): VueFormatterServiceOptions => {
  const vueFormatterOptions: VueFormatterServiceOptions = {};
  const sysEnv = getSysEnv(getApiGatewayUrl(dom));

  if (sysEnv === 'tui') {
    vueFormatterOptions.VUE_AGW_ENDPOINT =
      'https://tui.vue-agw.assets.audi.com';
  } else if (sysEnv === 'pre') {
    vueFormatterOptions.VUE_AGW_ENDPOINT =
      'https://pre.vue-agw.assets.audi.com';
  } else {
    vueFormatterOptions.VUE_AGW_ENDPOINT = 'https://vue-agw.assets.audi.com';
  }

  return vueFormatterOptions;
};

const prepareWindow = function (): void {
  window.featureServiceDefinitions = window.featureServiceDefinitions || [];

  // if there is no datalayer we provide one. as convenience for the developer
  if (typeof window.digitalData === 'undefined') {
    window.digitalData = { event: [] };
  }
};

const getCookieServiceOption = function (): CookieServiceOptions {
  const logger = createLogger();
  let useFallbackCookieManagement = true;
  if (typeof window.SETUPS !== 'undefined') {
    const scopes = window.SETUPS.get('nemo.env.scopes');
    useFallbackCookieManagement = scopes && scopes.includes('COOKIE_POLICY');
  } else {
    logger.warn(
      '`window.SETUPS` is not defined. Fallback to fallback consent manager',
    );
  }

  return {
    consentManager: useFallbackCookieManagement
      ? createFallbackConsentManager()
      : createEnsightenConsentManager(),
  };
};

export {
  defaultLogoutRedirectUrl as __defaultLogoutRedirectUrl,
  getApiGatewayUrl,
  getAuthProxyUrls as __getAuthProxyUrls,
  getAuthOptions,
  getColaOptions,
  getConfiguratorLinkOptions,
  getCookieServiceOption,
  getCurrentCarlineOptions,
  getDisclaimerManagerOptions,
  getDpuApiGatewayUrl,
  getMarketContextApiGatewayUrl,
  getEnvConfigOptions,
  getGfaServiceConfig,
  getI18nOptions,
  getLocaleOptions,
  getMarketContextServiceOptions,
  getNavigationOptions,
  getPreviewToken as __getPreviewToken,
  getRenderModeOptions,
  getTrackingOptions,
  getVueFormatterOptions,
  prepareWindow,
};
